import React from 'react';
import { Helmet } from 'react-helmet-async';
import { CONTACT } from './siteConfig';

export const SITE = {
  name: 'BYD BSD Serpong',
  url: 'https://bydbsdserpong.com',
  defaultTitle: 'BYD BSD Serpong | Dealer Resmi BYD Tangerang Selatan',
  defaultDescription:
    'Dealer resmi BYD BSD Serpong untuk harga mobil BYD, promo, test drive, dan konsultasi pembelian bersama Junius.',
  defaultImage: 'https://bydbsdserpong.com/logo512.png',
  phone: CONTACT.phone,
  whatsapp: `https://wa.me/${CONTACT.whatsappNumber}`,
  instagram: 'https://instagram.com/bydbsdserpong',
  address: {
    streetAddress: 'BSD City Serpong Kavling De Park, Jl. BSD Raya Utama Lot 1',
    addressLocality: 'Pagedangan',
    addressRegion: 'Banten',
    postalCode: '15331',
    addressCountry: 'ID',
  },
  geo: {
    latitude: -6.2816308,
    longitude: 106.6368673,
  },
};

export const keywordSets = {
  localDealer: [
    'dealer BYD BSD Serpong',
    'dealer BYD Tangerang Selatan',
    'BYD BSD Serpong Junius',
    'harga BYD BSD Serpong',
    'promo BYD BSD',
    'test drive BYD BSD',
  ],
  pricing: [
    'harga BYD 2026',
    'harga BYD BSD Serpong',
    'harga BYD Atto 1',
    'harga BYD Sealion 7',
    'harga Danza D9',
    'harga Danza Z9 GT',
    'harga BYD M6',
    'promo mobil listrik BYD',
  ],
};

export function absoluteUrl(path = '') {
  if (!path) return SITE.url;
  if (/^https?:\/\//i.test(path)) return path;

  const cleanPath = String(path).replace(/^\/+/, '');
  return `${SITE.url}/${cleanPath}`;
}

export function localBusinessJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'AutoDealer',
    '@id': `${SITE.url}/#dealer`,
    name: SITE.name,
    url: SITE.url,
    image: SITE.defaultImage,
    telephone: SITE.phone,
    priceRange: '$$',
    address: {
      '@type': 'PostalAddress',
      ...SITE.address,
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: SITE.geo.latitude,
      longitude: SITE.geo.longitude,
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: [
          'Monday',
          'Tuesday',
          'Wednesday',
          'Thursday',
          'Friday',
          'Saturday',
          'Sunday',
        ],
        opens: '09:00',
        closes: '18:00',
      },
    ],
    areaServed: [
      'BSD Serpong',
      'Tangerang Selatan',
      'Tangerang',
      'Gading Serpong',
      'Alam Sutera',
      'Jakarta',
    ],
    brand: [
      { '@type': 'Brand', name: 'BYD' },
      { '@type': 'Brand', name: 'Danza' },
    ],
    sameAs: [SITE.instagram, SITE.whatsapp],
  };
}

export function websiteJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE.url}/#website`,
    name: SITE.name,
    url: SITE.url,
    publisher: {
      '@id': `${SITE.url}/#dealer`,
    },
  };
}

export function productJsonLd({
  name,
  description,
  image = SITE.defaultImage,
  path = '/',
  price,
  lowPrice,
  highPrice,
  offerCount,
  availability = 'https://schema.org/InStock',
}) {
  const offerUrl = absoluteUrl(path);
  const normalizedLowPrice = Number(lowPrice);
  const normalizedHighPrice = Number(highPrice);
  const normalizedPrice = Number(price);
  const hasAggregateOffer =
    Number.isFinite(normalizedLowPrice) &&
    normalizedLowPrice > 0 &&
    Number.isFinite(normalizedHighPrice) &&
    normalizedHighPrice > 0;
  const hasSingleOffer =
    Number.isFinite(normalizedPrice) && normalizedPrice > 0;
  const offers = hasAggregateOffer
    ? {
        '@type': 'AggregateOffer',
        url: offerUrl,
        priceCurrency: 'IDR',
        lowPrice: normalizedLowPrice,
        highPrice: normalizedHighPrice,
        offerCount: offerCount || 2,
        availability,
        seller: {
          '@id': `${SITE.url}/#dealer`,
        },
      }
    : hasSingleOffer
      ? {
          '@type': 'Offer',
          url: offerUrl,
          priceCurrency: 'IDR',
          price: normalizedPrice,
          availability,
          itemCondition: 'https://schema.org/NewCondition',
          seller: {
            '@id': `${SITE.url}/#dealer`,
          },
        }
      : null;

  if (!offers) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name,
    image: absoluteUrl(image),
    description,
    brand: {
      '@type': 'Brand',
      name: name.toLowerCase().includes('danza') ? 'Danza' : 'BYD',
    },
    offers,
  };
}

export function articleJsonLd({
  title,
  description,
  image = SITE.defaultImage,
  path,
  datePublished = '2026-01-01',
  dateModified,
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    image: [absoluteUrl(image)],
    mainEntityOfPage: absoluteUrl(path),
    datePublished,
    dateModified: dateModified || datePublished,
    author: {
      '@type': 'Person',
      name: 'Junius',
    },
    publisher: {
      '@id': `${SITE.url}/#dealer`,
    },
  };
}

export function breadcrumbJsonLd(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
}

function normalizeKeywords(keywords) {
  if (!keywords) return '';
  return Array.isArray(keywords) ? keywords.join(', ') : keywords;
}

function normalizeJsonLd(jsonLd) {
  if (!jsonLd) return [];
  return (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).filter(Boolean);
}

function PageSeo({
  title = SITE.defaultTitle,
  description = SITE.defaultDescription,
  keywords = keywordSets.localDealer,
  path = '/',
  image = SITE.defaultImage,
  type = 'website',
  robots = 'index, follow',
  jsonLd,
}) {
  const canonical = absoluteUrl(path);
  const imageUrl = absoluteUrl(image);
  const keywordContent = normalizeKeywords(keywords);
  const jsonLdItems = normalizeJsonLd(jsonLd);

  return (
    <Helmet prioritizeSeoTags>
      <html lang='id' />
      <title>{title}</title>
      <meta name='description' content={description} />
      {keywordContent && <meta name='keywords' content={keywordContent} />}
      <meta name='robots' content={robots} />
      <meta name='author' content='Junius - BYD BSD Serpong' />
      <meta name='geo.region' content='ID-BT' />
      <meta
        name='geo.placename'
        content='BSD Serpong, Tangerang Selatan, Indonesia'
      />
      <meta name='geo.position' content='-6.2816308;106.6368673' />
      <meta name='ICBM' content='-6.2816308, 106.6368673' />
      <link rel='canonical' href={canonical} />

      <meta property='og:locale' content='id_ID' />
      <meta property='og:site_name' content={SITE.name} />
      <meta property='og:title' content={title} />
      <meta property='og:description' content={description} />
      <meta property='og:url' content={canonical} />
      <meta property='og:type' content={type} />
      <meta property='og:image' content={imageUrl} />

      <meta name='twitter:card' content='summary_large_image' />
      <meta name='twitter:title' content={title} />
      <meta name='twitter:description' content={description} />
      <meta name='twitter:image' content={imageUrl} />

      {jsonLdItems.map((item, index) => (
        <script type='application/ld+json' key={index}>
          {JSON.stringify(item)}
        </script>
      ))}
    </Helmet>
  );
}

export default PageSeo;
